import type { InstanceId, ItemId, RoomId, Rotation, SurfaceTag, WallSide, ZoneId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { AcceptClause, ItemDef } from '@/core/content/schemas/items';
import type { GameSave, Placement, SlotContent } from '@/core/persistence/schema/v1';
import {
  RoomGeometry,
  allowedRotations,
  bboxOf,
  cellKey,
  chebyshevGap,
  rectCells,
  rotatedFootprint,
  slotKey,
  slotParent,
  wallFootCells,
  type Cell,
  type SlotKey,
} from './geometry';

/** Where a player wants to put an item (content-schemas §13.1). */
export type PlacementTarget =
  | { layer: 'floor' | 'object'; roomId: RoomId; x: number; y: number; rotation: Rotation }
  | { layer: 'wall'; roomId: RoomId; wall: WallSide; offset: number }
  | { layer: 'surface'; roomId: RoomId; parentInstanceId: InstanceId; slotIndex: number };

export type PlaceFailure =
  | 'NOT_FOUND'
  | 'WRONG_LAYER'
  | 'INVALID_ROTATION'
  | 'OUT_OF_BOUNDS'
  | 'OVERLAP'
  | 'BLOCKED_ZONE'
  | 'OPENING'
  | 'SLOT_OCCUPIED'
  | 'NOT_ACCEPTED'
  | 'LAMP_ON_TOP'
  | 'WRONG_ROOM_FOR_KIND';

/** A lamp inside a cupboard lights nothing: lamps only take a piece's top slots. */
export function lampInside(def: ItemDef, parentDef: ItemDef, slotIndex: number): boolean {
  return def.tags.includes('lamp') && (parentDef.slots?.inner?.includes(slotIndex) ?? false);
}

export type PlaceCheck = { ok: true } | { ok: false; reason: PlaceFailure };

/** Footprint of something in a room, as seen by right-spot rules. Surface items borrow their parent's cells. */
export interface PlacedGeom {
  cells: Cell[];
  layer: 'floor' | 'object' | 'wall' | 'surface';
  surfaceTag?: SurfaceTag;
}

const geometryCache = new WeakMap<object, Map<string, RoomGeometry>>();

/** Geometry of a room with its unlocked expansions (cached per layout object). */
export function roomGeometry(content: ContentRegistry, state: GameSave, roomId: RoomId): RoomGeometry {
  const layout = content.rooms.get(roomId);
  if (!layout) throw new Error(`Unknown room ${roomId}`);
  const expansions = state.rooms[roomId].expansions;
  let byExp = geometryCache.get(layout);
  if (!byExp) {
    byExp = new Map();
    geometryCache.set(layout, byExp);
  }
  const key = [...expansions].sort().join('|');
  let geo = byExp.get(key);
  if (!geo) {
    geo = new RoomGeometry(layout, expansions);
    byExp.set(key, geo);
  }
  return geo;
}

function itemOf(content: ContentRegistry, state: GameSave, instanceId: InstanceId): ItemDef | undefined {
  const inst = state.inventory.instances[instanceId];
  return inst ? content.items.get(inst.itemId) : undefined;
}

/** Cells covered by a floor/object/wall placement. */
export function placementCells(def: ItemDef, p: Placement): Cell[] {
  if (p.layer === 'wall') return wallFootCells(p.wall, p.offset, def.wallSpan ?? 1);
  const fp = rotatedFootprint(def, p.rotation);
  return rectCells(p.x, p.y, fp.w, fp.h);
}

/** Geometry of a placement target (also used to preview a ghost before it is committed). */
export function targetGeom(
  content: ContentRegistry,
  state: GameSave,
  def: ItemDef,
  target: PlacementTarget,
): PlacedGeom | undefined {
  if (target.layer === 'surface') {
    const parent = state.rooms[target.roomId].placements[target.parentInstanceId];
    const parentDef = itemOf(content, state, target.parentInstanceId);
    if (!parent || !parentDef || parent.layer === 'wall') return undefined;
    return {
      cells: placementCells(parentDef, parent),
      layer: 'surface',
      surfaceTag: parentDef.slots?.surfaceTag,
    };
  }
  if (target.layer === 'wall') {
    return { cells: wallFootCells(target.wall, target.offset, def.wallSpan ?? 1), layer: 'wall' };
  }
  const fp = rotatedFootprint(def, target.rotation);
  return { cells: rectCells(target.x, target.y, fp.w, fp.h), layer: target.layer };
}

/** Every placed instance in a room with its geometry; surface items use their parent's cells. */
export function placedGeoms(
  content: ContentRegistry,
  state: GameSave,
  roomId: RoomId,
): { instanceId: InstanceId; itemId: ItemId; geom: PlacedGeom }[] {
  const room = state.rooms[roomId];
  const out: { instanceId: InstanceId; itemId: ItemId; geom: PlacedGeom }[] = [];
  for (const p of Object.values(room.placements)) {
    const def = itemOf(content, state, p.instanceId);
    if (!def) continue;
    out.push({
      instanceId: p.instanceId,
      itemId: def.id,
      geom: { cells: placementCells(def, p), layer: p.layer, surfaceTag: def.slots?.surfaceTag },
    });
  }
  for (const [key, contentRef] of Object.entries(room.slots) as [SlotKey, SlotContent][]) {
    if (contentRef.kind !== 'instance') continue;
    const parentId = slotParent(key);
    const parent = room.placements[parentId];
    const parentDef = itemOf(content, state, parentId);
    const def = itemOf(content, state, contentRef.instanceId);
    if (!parent || !parentDef || !def) continue;
    out.push({
      instanceId: contentRef.instanceId,
      itemId: def.id,
      geom: {
        cells: placementCells(parentDef, parent),
        layer: 'surface',
        surfaceTag: parentDef.slots?.surfaceTag,
      },
    });
  }
  return out;
}

export function accepts(clauses: readonly AcceptClause[], def: ItemDef): boolean {
  return clauses.some(
    (c) =>
      (!c.kinds || c.kinds.includes(def.kind)) &&
      (!c.layers || c.layers.includes(def.layer)) &&
      (!c.categories || (def.productCategory !== undefined && c.categories.includes(def.productCategory))) &&
      (!c.itemIds || c.itemIds.includes(def.id)) &&
      (!c.tags || c.tags.some((t) => def.tags.includes(t))),
  );
}

/**
 * Placement validity (content-schemas §14.13). `instanceId` is the item being placed or moved; its current
 * placement and its own slot are ignored for overlap checks so moving an item one cell is always possible.
 */
export function canPlace(
  content: ContentRegistry,
  state: GameSave,
  instanceId: InstanceId,
  target: PlacementTarget,
): PlaceCheck {
  const def = itemOf(content, state, instanceId);
  if (!def) return { ok: false, reason: 'NOT_FOUND' };
  if (def.kind === 'product') return { ok: false, reason: 'WRONG_LAYER' };
  const room = state.rooms[target.roomId];
  const geo = roomGeometry(content, state, target.roomId);

  if (target.layer === 'surface') {
    if (def.layer !== 'surface') return { ok: false, reason: 'WRONG_LAYER' };
    if (target.parentInstanceId === instanceId) return { ok: false, reason: 'NOT_ACCEPTED' };
    const parent = room.placements[target.parentInstanceId];
    const parentDef = itemOf(content, state, target.parentInstanceId);
    if (!parent || !parentDef?.slots) return { ok: false, reason: 'NOT_ACCEPTED' };
    if (target.slotIndex < 0 || target.slotIndex >= parentDef.slots.count)
      return { ok: false, reason: 'OUT_OF_BOUNDS' };
    if (!accepts(parentDef.slots.accepts, def)) return { ok: false, reason: 'NOT_ACCEPTED' };
    if (lampInside(def, parentDef, target.slotIndex)) return { ok: false, reason: 'LAMP_ON_TOP' };
    const occupant = room.slots[slotKey(target.parentInstanceId, target.slotIndex)];
    if (occupant && !(occupant.kind === 'instance' && occupant.instanceId === instanceId)) {
      return { ok: false, reason: 'SLOT_OCCUPIED' };
    }
    return { ok: true };
  }

  if (target.layer === 'wall') {
    if (def.layer !== 'wall') return { ok: false, reason: 'WRONG_LAYER' };
    const span = def.wallSpan ?? 1;
    const length = geo.wallLength[target.wall];
    if (target.offset < 0 || target.offset + span > length) return { ok: false, reason: 'OUT_OF_BOUNDS' };
    for (let i = 0; i < span; i++) {
      if (geo.openings[target.wall].has(target.offset + i)) return { ok: false, reason: 'OPENING' };
    }
    for (const p of Object.values(room.placements)) {
      if (p.instanceId === instanceId || p.layer !== 'wall' || p.wall !== target.wall) continue;
      const other = itemOf(content, state, p.instanceId);
      const otherSpan = other?.wallSpan ?? 1;
      if (target.offset < p.offset + otherSpan && p.offset < target.offset + span)
        return { ok: false, reason: 'OVERLAP' };
    }
    return { ok: true };
  }

  if (def.layer !== target.layer) return { ok: false, reason: 'WRONG_LAYER' };
  if (!allowedRotations(def.rotations).includes(target.rotation))
    return { ok: false, reason: 'INVALID_ROTATION' };
  const fp = rotatedFootprint(def, target.rotation);
  const cells = rectCells(target.x, target.y, fp.w, fp.h);
  if (!cells.every((c) => geo.isFloor(c))) return { ok: false, reason: 'OUT_OF_BOUNDS' };
  if (target.layer === 'object' && cells.some((c) => geo.blockedForObjects.has(cellKey(c.x, c.y)))) {
    return { ok: false, reason: 'BLOCKED_ZONE' };
  }
  const taken = new Set<string>();
  for (const p of Object.values(room.placements)) {
    if (p.instanceId === instanceId || p.layer !== target.layer) continue;
    const other = itemOf(content, state, p.instanceId);
    if (!other) continue;
    for (const c of placementCells(other, p)) taken.add(cellKey(c.x, c.y));
  }
  if (target.layer === 'object') {
    for (const box of Object.values(state.inventory.boxes)) {
      if (!box.opened && box.roomId === target.roomId) taken.add(cellKey(box.x, box.y));
    }
  }
  if (cells.some((c) => taken.has(cellKey(c.x, c.y)))) return { ok: false, reason: 'OVERLAP' };
  return { ok: true };
}

/** Zones a target touches (for ITEM_PLACED.zones and presenters). */
export function zonesForTarget(
  content: ContentRegistry,
  state: GameSave,
  def: ItemDef,
  target: PlacementTarget,
): ZoneId[] {
  const geom = targetGeom(content, state, def, target);
  return geom ? roomGeometry(content, state, target.roomId).zonesOf(geom.cells) : [];
}

/**
 * “Đúng chỗ” (content-schemas §14.2): any rule (OR) whose atoms all hold (AND). Works for committed
 * placements and for ghost previews, because it only needs a target.
 */
export function isRightSpot(
  content: ContentRegistry,
  state: GameSave,
  instanceId: InstanceId,
  target: PlacementTarget,
): boolean {
  const def = itemOf(content, state, instanceId);
  if (!def || def.homeTags.length === 0) return false;
  const geom = targetGeom(content, state, def, target);
  if (!geom) return false;
  const geo = roomGeometry(content, state, target.roomId);
  const maxGap = content.balance.nearMaxGapTiles;
  let others: ReturnType<typeof placedGeoms> | undefined;

  return def.homeTags.some((rule) =>
    rule.all.every((atom) => {
      switch (atom.kind) {
        case 'zone': {
          const zone = geo.zones.get(atom.zone);
          return zone !== undefined && geom.cells.some((c) => zone.has(cellKey(c.x, c.y)));
        }
        case 'on':
          return geom.layer === 'surface' && geom.surfaceTag === atom.surfaceTag;
        case 'wall':
          return geom.layer === 'wall';
        case 'wallAdjacent':
          return (
            (geom.layer === 'floor' || geom.layer === 'object') && geom.cells.some((c) => geo.touchesWall(c))
          );
        case 'near': {
          others ??= placedGeoms(content, state, target.roomId);
          const mine = bboxOf(geom.cells);
          return others.some(
            (o) =>
              o.instanceId !== instanceId &&
              content.items.get(o.itemId)?.tags.includes(atom.tag) === true &&
              chebyshevGap(mine, bboxOf(o.geom.cells)) <= maxGap,
          );
        }
      }
    }),
  );
}
